import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { MapPin } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import { PriceConverter } from '@/components/molecules/price-converter';
import type { CurrencyType } from '@/lib/utils/price.utils';
import type { ShippingRateFormData } from '../types/shipping-form.types';

interface ShippingRatePricingProps {
  form: UseFormReturn<ShippingRateFormData>;
  showPricing: boolean;
  isPickup: boolean;
}

export function ShippingRatePricing({ form, showPricing, isPickup }: ShippingRatePricingProps) {
  const { t } = useTranslation();
  const watchPrice = form.watch('price');
  const watchCurrency = form.watch('currency');

  if (isPickup) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <MapPin className="size-5 text-muted-foreground shrink-0" />
            <p className="text-sm">
              {t('shipping.pickupFreeMessage', { defaultValue: 'Pickup delivery is free - no pricing required.' })}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!showPricing) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shipping.pricing', { defaultValue: 'Pricing' })}</CardTitle>
        <CardDescription>
          {t('shipping.pricingDesc', { defaultValue: 'Set the price customers will pay for this shipping option.' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shipping.price', { defaultValue: 'Price' })} *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchPrice > 0 && watchCurrency && (
          <PriceConverter
            price={watchPrice}
            sourceCurrency={watchCurrency as CurrencyType}
            compact
          />
        )}
      </CardContent>
    </Card>
  );
}
